import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import { toasts } from "../components/AppToast";
import Layout from "../components/Layout";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiMail,
  FiUser,
  FiSearch,
} from "react-icons/fi";

export default function ManageRegularClients() {
  const navigate = useNavigate();

  // Table states
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("0");

  // Inner data states
  const [innerData, setInnerData] = useState([]);
  const [innerLoading, setInnerLoading] = useState(false);

  // View modal states
  const [seasonView, setSeasonView] = useState(null);
  const [regularClientView, setRegularClientView] = useState(null);

  // Delete modal states
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Auth check
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login", {
        replace: true,
      });

      return;
    }
  }, [navigate]);

  // Fetch regular clients
  const fetchData = useCallback(
    async (pg = 1, q = "", status = "0") => {
      setLoading(true);

      try {
        // Get regular clients from API
        const res = await API.get("/regularclients", {
          params: {
            page: pg,
            limit: 10,
            search: q,
            status,
          },
        });

        const responseData = res.data || {};

        // Set table data
        setData(Array.isArray(responseData.data) ? responseData.data : []);

        // Set pagination
        setTotalPages(
          Number(responseData.totalPages) > 0
            ? Number(responseData.totalPages)
            : 1
        );

        setTotal(Number(responseData.total) || 0);
      } catch (error) {
        // Handle API error
        console.error("Failed to fetch regular clients:", error);

        toasts.error(
          error.response?.data?.message || "Failed to load Regular Clients"
        );

        setData([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        // Stop loading
        setLoading(false);
      }
    },
    []
  );

  // Fetch table data when page, search or status changes
  useEffect(() => {
    const timer = setTimeout(
      () => {
        fetchData(page, search.trim(), selectedStatus);
      },
      search ? 350 : 0
    );

    return () => clearTimeout(timer);
  }, [page, search, selectedStatus, fetchData]);

  // Fetch season/inner data
  const fetchInnerData = useCallback(async (regclientId) => {
    if (!regclientId) {
      setInnerData([]);
      return;
    }

    setInnerLoading(true);

    try {
      // Get client season details
      const res = await API.get(`/regularclients/inner/${regclientId}`);

      setInnerData(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      // Handle inner data error
      console.error("Failed to fetch client details:", error);

      setInnerData([]);

      toasts.error(
        error.response?.data?.message || "Failed to load client details"
      );
    } finally {
      setInnerLoading(false);
    }
  }, []);

  // Open regular client view
  const openRegularClientView = (client) => {
    setRegularClientView(client);
    setSeasonView(null);

    fetchInnerData(client?.regclient_id);
  };

  // Open season discount view
  const openSeasonView = (client) => {
    setSeasonView(client);
    setRegularClientView(null);

    fetchInnerData(client?.regclient_id);
  };

  // Open add regular client page
  const openAdd = () => {
    navigate("/manage-regular-clients/add");
  };

  // Edit regular client
  const handleEdit = (client) => {
    if (!client?.regclient_id) {
      toasts.error("Invalid client ID");
      return;
    }

    navigate("/manage-regular-clients/edit", {
      state: {
        regclient_id: client.regclient_id,
        client: client,
      },
    });
  };

  // Open send mail page
  const handleSendMailPage = (client) => {
    if (!client) {
      toasts.error("Invalid client");
      return;
    }

    if (!client.regclient_id) {
      toasts.error("Invalid client ID");
      return;
    }

    navigate("/manage-regular-clients/send-mail", {
      state: {
        client: client,
      },
    });
  };

  // Toggle client status
  const handleToggle = async (row) => {
    if (!row?.regclient_id) {
      toasts.error("Invalid client ID");
      return;
    }

    const newStatus = Number(row.status) === 1 ? 2 : 1;

    try {
      // Update client status
      await API.put(`/regularclients/status/${row.regclient_id}`, {
        status: newStatus,
      });

      toasts.success(
        newStatus === 1 ? "Client activated" : "Client deactivated"
      );

      // Refresh table
      await fetchData(page, search.trim(), selectedStatus);
    } catch (error) {
      // Handle status update error
      console.error("Status update failed:", error);

      toasts.error(error.response?.data?.message || "Failed to update status");
    }
  };

  // Open delete modal
  const openDeleteModal = (client) => {
    if (!client?.regclient_id) {
      toasts.error("Invalid client ID");
      return;
    }

    setDelModal(client);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // Confirm client deletion
  const confirmDelete = async () => {
    if (delSaving) {
      return;
    }

    if (!delModal?.regclient_id) {
      toasts.error("Invalid client ID");
      return;
    }

    try {
      setDelSaving(true);

      // Delete client
      await API.delete(`/regularclients/${delModal.regclient_id}`);

      toasts.success("Client deleted successfully");

      setDelModal(null);

      // Move to previous page when deleting the last item
      const nextPage = data.length === 1 && page > 1 ? page - 1 : page;

      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        await fetchData(page, search.trim(), selectedStatus);
      }
    } catch (error) {
      // Handle delete error
      console.error("Delete client error:", error);

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // Close regular client view
  const closeRegularClientView = () => {
    setRegularClientView(null);
    setInnerData([]);
  };

  // Close season view
  const closeSeasonView = () => {
    setSeasonView(null);
    setInnerData([]);
  };

  return (
    <Layout activePage="Regular Clients Details">
      <main className="dashboard-main">
        {/* Table card */}
        <div className="table-card">
          {/* Header and filters */}
          <div className="table-card-head">
            {/* Search */}
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                placeholder="Search client..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Status filter */}
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(1);
              }}
              className="season-input"
            >
              <option value="0">Select Status</option>
              <option value="1">Active</option>
              <option value="2">Inactive</option>
            </select>

            {/* Add client */}
            <button type="button" className="um-btn-primary" onClick={openAdd}>
              <span className="um-add-icon">＋</span>
              Add Regular Client
            </button>
          </div>

          {/* Client table */}
          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Email Address</th>
                  <th className="um-th text-center">Client Name</th>
                  <th className="um-th text-center">Client ID</th>
                  <th className="um-th text-center">Status</th>
                  <th className="um-th text-center">
                    Season Wise
                    <br />
                    Discount Details
                  </th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {/* Loading state */}
                {loading ? (
                  Array.from({
                    length: 5,
                  }).map((_, i) => (
                    <tr key={`loading-${i}`}>
                      {Array.from({
                        length: 7,
                      }).map((_, j) => (
                        <td key={`skeleton-${j}`} className="um-td text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  /* Empty state */
                  <tr>
                    <td
                      colSpan={7}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">👤</div>

                      <div className="empty-title">
                        {search
                          ? "No matching clients found"
                          : "No regular clients found"}
                      </div>

                      <div className="empty-sub">
                        {search
                          ? "Try another search"
                          : "Use the Add Regular Client button to add a client"}
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Client records */
                  data.map((row, i) => {
                    const fullName = [
                      row.regclient_title,
                      row.regclient_name,
                      row.regclient_familyname,
                    ]
                      .filter(Boolean)
                      .join(" ")
                      .trim();

                    const isActive = Number(row.status) === 1;

                    return (
                      <tr
                        key={row.regclient_id}
                        className={`um-row ${
                          i % 2 === 0 ? "even-row" : "odd-row"
                        }`}
                      >
                        {/* Number */}
                        <td className="um-td text-center">
                          {(page - 1) * 10 + i + 1}
                        </td>

                        {/* Email */}
                        <td className="um-td text-center">
                          <span className="car-name-badge">
                            {row.regclient_email || "—"}
                          </span>
                        </td>

                        {/* Client name */}
                        <td className="um-td text-center">
                          <span className="car-name-badge">
                            {fullName || "—"}
                          </span>
                        </td>

                        {/* Client ID */}
                        <td className="um-td text-center">
                          <span className="car-group-badge">
                            {row.client_id || "—"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="um-td">
                          <div className="status-wrap">
                            <div
                              className={`status-toggle ${
                                isActive ? "status-active" : "status-inactive"
                              }`}
                              onClick={() => handleToggle(row)}
                              aria-label={
                                isActive
                                  ? "Deactivate client"
                                  : "Activate client"
                              }
                            >
                              <span
                                className={`status-knob ${
                                  isActive ? "knob-active" : "knob-inactive"
                                }`}
                              />
                            </div>

                            <span
                              className={`status-text ${
                                isActive ? "text-active" : "text-inactive"
                              }`}
                            >
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td>

                        {/* Season discount */}
                        <td className="um-td text-center">
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View Season Discount"
                            onClick={() => openSeasonView(row)}
                          >
                            <FiEye size={15} />
                          </button>
                        </td>

                        {/* Actions */}
                        <td className="um-td text-center">
                          <div className="action-buttons">
                            {/* View */}
                            <button
                              type="button"
                              className="action-btn action-view"
                              title="View"
                              onClick={() => openRegularClientView(row)}
                            >
                              <FiEye size={15} />
                            </button>

                            {/* Send mail */}
                            <button
                              type="button"
                              className="action-btn action-edit"
                              title="Send Mail"
                              onClick={() => handleSendMailPage(row)}
                            >
                              <FiMail size={15} />
                            </button>

                            {/* Edit */}
                            <button
                              type="button"
                              className="action-btn action-edit"
                              title="Edit"
                              onClick={() => handleEdit(row)}
                            >
                              <FiEdit size={15} />
                            </button>

                            {/* Delete */}
                            <button
                              type="button"
                              className="action-btn action-delete delete-enabled"
                              title="Delete"
                              onClick={() => openDeleteModal(row)}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination">
              <span className="pagin-info">
                {total > 0
                  ? `Showing ${(page - 1) * 10 + 1}–${Math.min(
                      page * 10,
                      total
                    )} of ${total}`
                  : ""}
              </span>

              <div className="d-flex align-items-center gap-2">
                {/* Previous page */}
                <button
                  type="button"
                  className="pg-btn"
                  disabled={page === 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ← Prev
                </button>

                {/* Current page */}
                <span className="pagin-info">
                  Page {page} / {totalPages || 1}
                </span>

                {/* Next page */}
                <button
                  type="button"
                  className="pg-btn"
                  disabled={page >= totalPages || totalPages === 0 || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Regular client view modal */}
      {regularClientView && (
        <div className="um-overlay" onClick={closeRegularClientView}>
          <div
            className="um-modal um-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="um-modal-head">
              <h3 className="um-modal-title d-flex align-items-center gap-2">
                <FiUser size={24} />
                View Client Id & Discount
              </h3>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeRegularClientView}
              >
                ✕
              </button>
            </div>

            {/* Modal body */}
            <div className="um-modal-scroll">
              {/* Client details */}
              <div className="sec-div">
                <span>Regular Client Details</span>
              </div>

              <div className="row g-3">
                {[
                  ["Title", regularClientView.regclient_title],
                  ["Client Name", regularClientView.regclient_name],
                  ["Family Name", regularClientView.regclient_familyname],
                  ["Street Number", regularClientView.regclient_streetno],
                  ["Zip Code", regularClientView.regclient_postcode],
                  ["Town", regularClientView.regclient_town],
                  ["Country", regularClientView.regclient_country],
                  ["Mobile No", regularClientView.regclient_mobno],
                  ["Phone No", regularClientView.regclient_phoneno],
                  ["Email Address", regularClientView.regclient_email],
                  ["Client Id", regularClientView.client_id],
                  [
                    "Status",
                    Number(regularClientView.status) === 1
                      ? "Active"
                      : "Inactive",
                    Number(regularClientView.status) === 1
                      ? "viewnameChipAct"
                      : "viewnameChipInAct",
                  ],
                ].map(([k, v, valueClass]) => (
                  <div key={k} className="col-xl-3 col-lg-3 col-md-6 col-12">
                    <div className="form-label">{k}</div>

                    <div className="form-value">
                      <span className={valueClass || ""}>{v || "—"}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Season details */}
              <div className="sec-div mt-4 mb-3">
                <span>
                  Season Wise Per Day Price <i>Discount (in %)</i>
                </span>
              </div>

              {innerLoading ? (
                <div className="text-center text-muted py-4">
                  Loading season details...
                </div>
              ) : innerData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle mb-0">
                    <thead className="table-light um-table-head">
                      <tr>
                        <th>Season Name</th>
                        <th>Discount (in %)</th>
                      </tr>
                    </thead>

                    <tbody>
                      {innerData.map((item, index) => (
                        <tr key={item.season_id || index}>
                          <td>
                            <span
                              className="form-label"
                              style={{
                                fontWeight: "bold",
                              }}
                            >
                              {item.season_name || "—"}
                            </span>
                          </td>

                          <td>
                            <span className="badge badge-success">
                              {item.season_discount ?? "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted bg-light rounded p-4">
                  No season details found
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeRegularClientView}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Season discount view modal */}
      {seasonView && (
        <div className="um-overlay" onClick={closeSeasonView}>
          <div
            className="um-modal um-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="um-modal-head">
              <h3 className="um-modal-title d-flex align-items-center gap-2">
                <FiUser size={24} />
                View Client Id & Discount
              </h3>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeSeasonView}
              >
                ✕
              </button>
            </div>

            {/* Modal body */}
            <div className="um-modal-scroll">
              {/* Client details heading */}
              <div className="row g-3">
                <div className="col-12">
                  <div className="sec-div">
                    <span>Regular Client Details</span>
                  </div>
                </div>
              </div>

              {/* Client information */}
              <div className="table-responsive mt-3">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <tbody>
                    {[
                      [
                        "Client Name",
                        [
                          seasonView.regclient_title,
                          seasonView.regclient_name,
                          seasonView.regclient_familyname,
                        ]
                          .filter(Boolean)
                          .join(" ")
                          .trim(),
                      ],
                      ["Email Address", seasonView.regclient_email],
                      ["Client Id", seasonView.client_id],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <th
                          className="bg-light"
                          style={{
                            width: "35%",
                          }}
                        >
                          <span
                            className="form-label"
                            style={{
                              fontWeight: "bold",
                            }}
                          >
                            {k}
                          </span>
                        </th>

                        <td>{v || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Season details */}
              <div className="sec-div mt-4 mb-3">
                <span>
                  Season Wise Per Day Price Discount{" "}
                  <i>Discount (in %)</i>
                </span>
              </div>

              {innerLoading ? (
                <div className="text-center text-muted py-4">
                  Loading season details...
                </div>
              ) : innerData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle mb-0">
                    <thead className="table-light um-table-head">
                      <tr>
                        <th>Season Name</th>
                        <th>Discount (in %)</th>
                      </tr>
                    </thead>

                    <tbody>
                      {innerData.map((item, index) => (
                        <tr key={item.season_id || index}>
                          <td>
                            <span
                              className="form-label"
                              style={{
                                fontWeight: "bold",
                              }}
                            >
                              {item.season_name || "—"}
                            </span>
                          </td>

                          <td>
                            <span className="badge badge-success">
                              {item.season_discount ?? "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted bg-light rounded p-4">
                  No season details found
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeSeasonView}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete client modal */}
      {delModal && (
        <div className="um-overlay" onClick={closeDeleteModal}>
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">
              {/* Delete icon */}
              <div className="delete-icon">
                <FiTrash2 size={32} />
              </div>

              {/* Delete title */}
              <h2 className="delete-title">Delete Client?</h2>

              {/* Delete description */}
              <p className="delete-description">
                Are you sure you want to delete{" "}
                <span className="badge badge-success">
                  "
                  {[
                    delModal.regclient_title,
                    delModal.regclient_name,
                    delModal.regclient_familyname,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || "this client"}
                  "
                </span>
                ?
                <br />
                This will permanently remove this client.
                <br />
                <strong>This action cannot be undone.</strong>
              </p>

              {/* Delete actions */}
              <div className="delete-actions">
                {/* Cancel */}
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={delSaving}
                >
                  Cancel
                </button>

                {/* Confirm delete */}
                <button
                  type="button"
                  disabled={delSaving}
                  className="delete-confirm-btn"
                  onClick={confirmDelete}
                >
                  {delSaving ? (
                    <>
                      <span className="spinner" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

     
    </Layout>
  );
}
